import { otherResultFormula } from "@/orm/catalogs/OtherResults"
import { DateFormat, ds, tcs } from "@/plugins/i18n-formatted"
import nvl from "@/utils/nvl"
import EventBoat from "@model/EventBoat"
import EventClass from "@model/EventClass"
import EventRace from "@model/EventRace"
import Sandbox from "@nyariv/sandboxjs"
import { Model, useRepo } from 'pinia-orm'
import { DateCast } from "pinia-orm/casts"
import { Attr, BelongsTo, Cast, Num, Str, Uid } from 'pinia-orm/decorators'

export default class EventResult extends Model {
    static entity = 'events-results'

    @Uid() declare id: string
    @Uid() declare eventRaceId: string
    @BelongsTo( () => EventRace, 'eventRaceId' ) declare race: EventRace
    @Uid() declare eventBoatId: string
    @BelongsTo( () => EventBoat, 'eventBoatId' ) declare boat: EventBoat
    @Num( null ) declare order: number | null
    @Str( null ) declare other: string | null
    @Num( null ) declare attr: number | null
    @Num( 0 ) declare counter: number
    @Cast( () => DateCast ) @Attr( '' ) declare finishTime: string | null
    @Cast( () => DateCast ) @Attr( '' ) declare ts: string | null

    get dump () {
        return {
            eventBoatId: this.eventBoatId,
            _boat: { country: this.boat?.country, sailNumber: this.boat?.sailNumber },
            order: this.order,
            other: this.other,
            attr: this.attr,
            counter: this.counter,
            _points: this.points,
            finishTime: this.finishTime === null ? null : ds( this.finishTime, DateFormat.System ),
            ts: this.ts === null ? null : ds( this.ts, DateFormat.System )
        }
    }

    get orderInclHandicap () {
        if ( !this.isHandicap )
            return this.order

        const simpleEventClass = useRepo( EventClass ).where( 'eventId', this.boat.eventId ).where( 'boatClassId', this.boat.boatClassId ).first()

        const startTime = this.race.startTime( this.boat.boatClassId )

        const ordered = this.race.results.filter( res => res.boat.boatClassId === this.boat.boatClassId && res.other === null )
            .map( ( item ) => item.calculatedTime( startTime, null, simpleEventClass ) ).sort( ( a, b ) => ( a - b ) )

        const calcTime = this.calculatedTime( startTime, null, simpleEventClass )

        const newOrder =  ( ordered.indexOf( calcTime ) + ordered.lastIndexOf( calcTime ) ) / 2 + 1

        return Math.round( newOrder * 10.0 ) / 10
    }

    get isHandicap () {
        return this.finishTime !== null && this.finishTime !== undefined && !isNaN( this.finishTime.getTime() )
    }

    realTime ( startTime = null ) {
        if ( !this.isHandicap )
            return null

        if ( startTime === null ) {
            if ( this.race !== null && this.boat !== null )
                startTime = this.race.startTime( this.boat.boatClassId )
            else {
                const race = useRepo( EventRace ).find( this.eventRaceId )

                startTime = race.startTime( this.boat.boatClassId )
            }
        }

        if ( startTime === null || this.finishTime === null )
            return 0

        return Math.round( ( this.finishTime.getTime() - startTime.getTime() ) / 1000.0 )
    }

    calculatedTime ( startTime = null, coefficient = null, eventClass = null ) {
        const realTime = this.realTime( startTime )

        if ( realTime === null )
            return null

        if ( coefficient === null )
            coefficient = this.boat.calcHandicapCoef( eventClass )

        return realTime * coefficient
//        return Math.round( realTime * coefficient )
    }

    get points (): number {
        return this.calculatePoints()
    }

    calculatePoints ( totalBoats = null ): number {
        if ( this.order !== null && this.other === null )
            return this.orderInclHandicap

        const formula = otherResultFormula( this.other )

        const boats = totalBoats === null ? this.race.totalBoats( this.boat?.boatClassId ) : totalBoats
        const order = nvl( this.orderInclHandicap, boats )
        const attr = nvl( this.attr, boats )

        const sandbox = new Sandbox()
        const exec = sandbox.compile( 'return ' + formula.replace( /attr\[.*?\]/, 'attr' ) )

        const result = exec( {
            boats: boats,
            order: order,
            attr: attr
        } ).run()

        return result
    }

    get result () {
        return nvl( this.other, this.orderInclHandicap )
    }

    set result ( val ) {
        // ignore
    }

    orderTo ( comparable ) {
        const orderA = this.order
        const orderB = comparable.order

        if ( this.isHandicap && orderA !== null && orderB !== null )
            return this.finishTime > comparable.finishTime ? 1 : ( this.finishTime < comparable.finishTime ? -1 : 0 )

        if ( orderA !== null && orderB !== null )
            return orderA - orderB

        if ( orderA !== null )
            return -1

        if ( orderB !== null )
            return 1

        if ( this.other === null || comparable.other === null )
            return 0

        return this.other > comparable.other ? 1 : ( this.other < comparable.other ? -1 : 0 )
    }

    get attrInfo () {
        if ( this.attr === null )
            return ''

        if ( this.other === null )
            return this.attr

        const formula = otherResultFormula( this.other )

        if ( formula === null )
            return this.attr

        const matches = formula.match( /attr\[\s*(percent|points).*?\]/ )

        if ( !matches )
            return this.attr

        const attrType = matches[ 1 ]

        return attrType === 'points' ? tcs( 'pointsNo', this.attr ) : Math.round( this.attr * 100 ) + " %"
    }

    static piniaOptions = {
        persist: true
    }
}
